import { OktaUserInfo } from '@/app/core/auth';
import { CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import { Divider, Flex, Grid, GridItem, Text, VStack } from '@chakra-ui/react';
import { getSubCodesForProduct, isSuperAdmin } from '@ssar-app/domain';
import { Entitlement, isAuthzAnyValue } from '@ascend/authz';

interface ProfileDetails {
  basicDetails: {
    firstName: string;
    lastName: string;
    userName: string;
    oktaSub: string;
  };
  appDetails: Array<{
    feature: string;
    role: string;
    subCodes: string;
  }>;
}

export const UserProfile = () => {
  const oktaUserProfileRef = useRef(OktaUserInfo.getOktaUserInfo());
  const [userProfileData, setUserProfileData] = useState<ProfileDetails>();
  const oktaUserProfile = oktaUserProfileRef.current;
  const featureAppRolesStrings = useMemo(() => {
    if (isSuperAdmin(oktaUserProfile)) {
      return [
        {
          feature: 'All Features',
          appRoles: 'All Roles',
          subCodes: 'All SubCodes',
        },
      ];
    }

    const entitlements = (oktaUserProfile.entitlements as Entitlement[]) || [];
    return (entitlements || []).map((a) => {
      const productSubcodes = getSubCodesForProduct(a.context, oktaUserProfile);
      return {
        feature: a.context,
        appRoles: isAuthzAnyValue(a.roles)
          ? 'All Roles'
          : a.roles.filter((role: string) => !!role).join(', '),
        subCodes: isAuthzAnyValue(productSubcodes)
          ? 'All SubCodes'
          : productSubcodes.join(', '),
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oktaUserProfile.entitlements]);

  useEffect(() => {
    const basicdetails = {
      firstName: oktaUserProfile.firstName,
      lastName: oktaUserProfile.lastName,
      userName: oktaUserProfile.userName,
      oktaSub: oktaUserProfile.sub,
    };

    const appDetails = featureAppRolesStrings?.map((item) => {
      return {
        feature: item.feature,
        role: item.appRoles,
        subCodes: item.subCodes,
      };
    });
    setUserProfileData({ basicDetails: basicdetails, appDetails: appDetails });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const labelNameStyles: CSSProperties = {
    color: 'black',
    border: '1px solid #eaeaea',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 4,
    backgroundColor: 'white',
  };

  return (
    <>
      <VStack alignItems={'start'} my='4' mx='6'>
        <Text fontSize='2xl' fontWeight='semibold' mb={2} color={'black'}>
          User Account
        </Text>
        <Divider mb={4} color='gray.400' />
        <Flex
          direction={'column'}
          padding='6'
          borderRadius={'md'}
          bg='white'
          mt='4'
        >
          <Text fontSize={'md'} fontWeight='semibold' textColor={'black'}>
            Basic Details
          </Text>
          <Grid
            templateColumns='repeat(4, 1fr)'
            justifyContent={'center'}
            mt='2'
          >
            <GridItem fontWeight={'semibold'} sx={labelNameStyles}>
              First Name
            </GridItem>
            <GridItem sx={labelNameStyles}>
              {userProfileData?.basicDetails?.firstName}
            </GridItem>
            <GridItem fontWeight={'semibold'} sx={labelNameStyles}>
              Last Name
            </GridItem>
            <GridItem sx={labelNameStyles}>
              {userProfileData?.basicDetails?.lastName}
            </GridItem>
            <GridItem fontWeight={'semibold'} sx={labelNameStyles}>
              Username
            </GridItem>
            <GridItem sx={labelNameStyles}>
              {userProfileData?.basicDetails?.userName}
            </GridItem>
            <GridItem fontWeight={'semibold'} sx={labelNameStyles}>
              Okta Sub
            </GridItem>
            <GridItem sx={labelNameStyles}>
              {userProfileData?.basicDetails?.oktaSub}
            </GridItem>
          </Grid>

          <Text
            fontSize={'md'}
            fontWeight='semibold'
            textColor={'black'}
            mt='6'
          >
            Feature Role Details
          </Text>
          <Grid
            templateColumns='repeat(3, 1fr)'
            justifyContent={'center'}
            mt='2'
          >
            <GridItem fontWeight={'semibold'} sx={labelNameStyles}>
              Feature
            </GridItem>
            <GridItem fontWeight={'semibold'} sx={labelNameStyles}>
              Role
            </GridItem>
            <GridItem fontWeight={'semibold'} sx={labelNameStyles}>
              Sub Codes
            </GridItem>
            {userProfileData?.appDetails?.map(({ feature, role, subCodes }) => {
              return (
                <>
                  <GridItem sx={labelNameStyles}>{feature}</GridItem>
                  <GridItem sx={labelNameStyles}>{role}</GridItem>
                  <GridItem sx={labelNameStyles}>{subCodes}</GridItem>
                </>
              );
            })}
          </Grid>
        </Flex>
      </VStack>
    </>
  );
};
UserProfile.whyDidYouRender = true;
